import type { LeagueContact, LeagueContactsResponse } from '@league/shared/contract';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';

import { Facts, MobileList, MobileRow, pacePhrase, Sheet, SHEET_DONE, SHEET_LINK } from '../../../components/MobileList';
import { PLAYER_ROW, PlayerTable, Td, Th } from '../../../components/PlayerTable';
import { SegmentedControl } from '../../../components/SegmentedControl';
import { ErrorNote } from '../../../components/ui';
import { paceLabel } from '../../../content/playing-preferences';
import { apiFetch } from '../../../lib/api';
import { comparePeople } from '../people-display';
import { RosterSummary } from '../RosterSummary';
import type { LeagueContext } from '../LeagueWorkspace';

const LISTS = [
  ['roster', 'Roster'],
  ['waitlist', 'Waitlist'],
  ['subs', 'Subs'],
] as const;

/* As the director's search box: the box carries the focus, not the input inside it. */
// eslint-disable-next-line no-restricted-syntax -- the wrapping label shows a 2px navy focus edge instead
const SEARCH_INPUT = 'min-w-0 flex-1 border-0 bg-transparent text-[14px] text-ink outline-none placeholder:text-ink-3';

/**
 * AN INSTRUCTOR'S ROSTER (owner, 2026-11-01): the same three lists as the director's, read-only, with
 * an email and a phone number so somebody who has not arrived can be reached. Nothing here changes
 * anything, and the names are plain text: the player pages are the director's.
 */
export function ContactsTab() {
  const { league } = useOutletContext<LeagueContext>();
  const [showing, setShowing] = useState<(typeof LISTS)[number][0]>('roster');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<LeagueContact | null>(null);

  const contacts = useQuery({
    queryKey: ['league-contacts', league.id],
    queryFn: () => apiFetch<LeagueContactsResponse>(`/api/leagues/${league.id}/contacts`),
  });

  if (contacts.isPending) return <p className="text-sm text-ink-3">Loading…</p>;
  if (contacts.isError) return <ErrorNote>Could not load the roster.</ErrorNote>;

  const needle = search.trim().toLowerCase();
  const people = contacts.data[showing]
    .filter((person) => `${person.firstName} ${person.lastName} ${person.email ?? ''}`.toLowerCase().includes(needle))
    .sort(comparePeople);
  const waitlist = showing === 'waitlist';

  return (
    <section className="flex flex-col gap-4">
      <RosterSummary seatsTaken={contacts.data.roster.length} playerCap={league.playerCap} waiting={contacts.data.waitlist.length} />

      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl label="Player list" options={LISTS} value={showing} onChange={setShowing} />
        <label className="flex min-h-11 min-w-[220px] flex-1 items-center gap-2 rounded-[10px] border border-sand bg-paper px-3.5 has-[:focus-visible]:border-deep has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-deep">
          <span className="sr-only">Search players</span>
          <svg aria-hidden="true" viewBox="0 0 16 16" className="h-4 w-4 shrink-0 text-ink-3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <circle cx="7" cy="7" r="5" />
            <path d="M11 11l3.5 3.5" />
          </svg>
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search players" className={SEARCH_INPUT} />
        </label>
      </div>

      {people.length === 0 ? (
        <p className="rounded-md border border-dashed border-light px-5 py-8 text-center text-[13px] text-ink-3">
          {needle ? `Nobody here matches “${search.trim()}”.` : `No ${showing === 'roster' ? 'players' : showing === 'subs' ? 'subs' : 'one on the waitlist'} yet.`}
        </p>
      ) : (
        <>
          <PlayerTable head={<>{waitlist ? <Th>Queue</Th> : null}<Th>Player</Th><Th>Pace of play</Th><Th>Email</Th><Th>Phone</Th></>}>
            {people.map((person) => (
              <tr key={person.playerId} className={`${PLAYER_ROW} hover:bg-chalk`}>
                {waitlist ? <Td className="tabular text-[14px] text-ink-2">{person.waitlistPosition}</Td> : null}
                <Td kind="name" className="text-[16px] font-semibold text-ink">{person.firstName} {person.lastName}</Td>
                <Td className="whitespace-nowrap text-[14px] text-ink-2">{paceLabel(person.pace)}</Td>
                <Td className="text-[14px]"><Reach kind="mailto" value={person.email} /></Td>
                <Td className="whitespace-nowrap text-[14px]"><Reach kind="tel" value={person.phone} /></Td>
              </tr>
            ))}
          </PlayerTable>

          <MobileList>
            {people.map((person) => (
              <MobileRow
                key={person.playerId}
                firstName={person.firstName}
                lastName={person.lastName}
                onOpen={() => setOpen(person)}
                lines={[waitlist ? `#${person.waitlistPosition} in the queue · ${pace(person)}` : pace(person)]}
              />
            ))}
          </MobileList>
        </>
      )}

      {open ? (
        <Sheet
          open
          onClose={() => setOpen(null)}
          title={`${open.firstName} ${open.lastName}`}
          footer={<><span /><button type="button" className={SHEET_DONE} onClick={() => setOpen(null)}>Done</button></>}
        >
          <Facts items={[{ label: 'Pace of play', value: paceLabel(open.pace) }]} />
          <div className="flex flex-col gap-3 text-[15px]">
            <Reach kind="mailto" value={open.email} />
            <Reach kind="tel" value={open.phone} />
          </div>
        </Sheet>
      ) : null}
    </section>
  );
}

function pace(person: LeagueContact): string {
  return pacePhrase(paceLabel(person.pace), person.pace === 'unknown');
}

/** An email or a phone number that opens on a tap, or says there is none. */
function Reach({ kind, value }: { kind: 'mailto' | 'tel'; value: string | null }) {
  if (!value) return <span className="text-ink-3">{kind === 'tel' ? 'No phone' : 'No email'}</span>;
  return <a href={`${kind}:${value}`} className={`break-all ${SHEET_LINK}`}>{value}</a>;
}
